import type { JSX } from 'react';
import type {
  CompanyDetailResource,
  ContactResource,
  ConvertDealBody,
  ConvertDealResult,
  DealDetailResource,
  DirectoryCompanyResource,
} from '@majuscule/contracts';
import { useMutation, useQuery } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { Mail } from 'lucide-react';
import { useEffect, useState } from 'react';
import { internalFetch } from '../../lib/api';
import {
  crmSend,
  errorMessage,
  eurOrDash,
  euroInputError,
  MODEL_LABEL,
  nextMonthOf,
  parisToday,
  parseEuroInput,
} from '../../lib/crm';
import { Choices, MutationError, useCompany, useCrmRefresh } from './shared';
import { Callout } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { CopyButton } from '@/components/ui/code';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHead,
} from '@/components/ui/dialog';
import { LoadingState } from '@/components/ui/empty';
import { CheckRow, Field, FieldHint, Fields2, Fieldset, InputGroup } from '@/components/ui/field';
import { DateInput, MonthInput } from '@/components/ui/date-input';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { NativeSelect } from '@/components/ui/native-select';

/** « Passer en client » (ConvertDialog.dc.html), then the created accounts and mandate link. */
export function ConvertDialog({
  detail,
  open,
  onClose,
}: {
  detail: DealDetailResource;
  open: boolean;
  onClose: () => void;
}): JSX.Element {
  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        if (!next) {
          onClose();
        }
      }}
    >
      {open ? <ConvertContent detail={detail} onClose={onClose} /> : null}
    </Dialog>
  );
}

function ConvertContent({
  detail,
  onClose,
}: {
  detail: DealDetailResource;
  onClose: () => void;
}): JSX.Element {
  const company = useCompany(detail.company.id);
  const [result, setResult] = useState<ConvertDealResult | null>(null);

  if (result !== null) {
    return <ConvertResult result={result} onClose={onClose} />;
  }
  return (
    <DialogContent width={640}>
      <DialogHead
        title="Passer en client"
        description={`${detail.company.name}. L’affaire passe en Gagnée et l’abonnement est ajouté au compte facturé.`}
      />
      {company.data === undefined ? (
        <DialogBody>
          {company.isError ? (
            <Callout variant="destructive">Impossible de charger l’entreprise.</Callout>
          ) : (
            <LoadingState />
          )}
        </DialogBody>
      ) : (
        <ConvertForm detail={detail} company={company.data} onClose={onClose} onDone={setResult} />
      )}
    </DialogContent>
  );
}

/** Contact whose email receives invoices: role billing, else the primary contact. */
function billingContact(company: CompanyDetailResource): ContactResource | null {
  return (
    company.contacts.find(
      (contact) => contact.roles.includes('billing') && contact.email !== null,
    ) ??
    company.contacts.find((contact) => contact.primary && contact.email !== null) ??
    null
  );
}

type Target = 'new' | 'existing';

function ConvertForm({
  detail,
  company,
  onClose,
  onDone,
}: {
  detail: DealDetailResource;
  company: CompanyDetailResource;
  onClose: () => void;
  onDone: (result: ConvertDealResult) => void;
}): JSX.Element {
  const refresh = useCrmRefresh();
  const { deal } = detail;
  const today = parisToday();
  const recipient = billingContact(company);
  const canUseExisting = company.status === 'client' && company.accounts.length > 0;

  const [target, setTarget] = useState<Target>(canUseExisting ? 'existing' : 'new');
  const [clientId, setClientId] = useState(company.accounts[0]?.id ?? '');
  const [signedOn, setSignedOn] = useState(today);
  const [startMonth, setStartMonth] = useState(nextMonthOf(today));
  const [name, setName] = useState(company.name);
  const [legalName, setLegalName] = useState(company.legalName ?? company.name);
  const [billingEmail, setBillingEmail] = useState(recipient?.email ?? '');
  const [safetyCap, setSafetyCap] = useState('300');
  const [clientCap, setClientCap] = useState('');
  const [userCap, setUserCap] = useState('');
  const [address, setAddress] = useState({ street: '', zipCode: '', city: '', countryCode: 'FR' });
  const [mandate, setMandate] = useState(true);
  const [submitted, setSubmitted] = useState(false);

  // Billing address prefilled from the companies directory (used only when Qonto has no client yet).
  const directory = useQuery({
    queryKey: ['crm', 'directory', company.siren],
    queryFn: () =>
      internalFetch<DirectoryCompanyResource>(`/internal/crm/directory/${company.siren ?? ''}`),
    enabled: company.siren !== null && target === 'new',
    retry: false,
  });
  const found = directory.data?.billingAddress ?? null;
  useEffect(() => {
    if (found !== null) {
      setAddress((current) =>
        current.street === '' && current.zipCode === '' && current.city === ''
          ? { ...found }
          : current,
      );
    }
  }, [found]);

  const addressParts = [address.street, address.zipCode, address.city].map((part) => part.trim());
  const addressFilled = addressParts.every((part) => part !== '');
  const addressEmpty = addressParts.every((part) => part === '');

  const errors =
    target === 'existing'
      ? { clientId: clientId === '' ? 'Choisir le compte facturé.' : undefined }
      : {
          name: name.trim() === '' ? 'Indiquer le nom du compte.' : undefined,
          legalName: legalName.trim() === '' ? 'Indiquer la raison sociale.' : undefined,
          billingEmail: /^\S+@\S+\.\S+$/.test(billingEmail.trim())
            ? undefined
            : 'Indiquer un email de facturation valide.',
          safetyCap: euroInputError(safetyCap, true),
          clientCap: euroInputError(clientCap),
          userCap: euroInputError(userCap),
          address:
            addressFilled || addressEmpty
              ? undefined
              : 'Adresse complète requise : rue, code postal et ville, ou aucune.',
        };
  const dateErrors = {
    signedOn: signedOn === '' ? 'Choisir la date de signature.' : undefined,
    startMonth: startMonth === '' ? 'Choisir le premier mois d’abonnement.' : undefined,
  };
  const valid = [...Object.values(errors), ...Object.values(dateErrors)].every(
    (error) => error === undefined,
  );
  const shown = (error: string | undefined): string | undefined => (submitted ? error : undefined);
  const fieldError = (key: string): string | undefined =>
    shown(Object.entries(errors).find(([k]) => k === key)?.[1]);

  const convert = useMutation({
    mutationFn: () => {
      const body: ConvertDealBody = {
        signedOn,
        subscriptionStartMonth: startMonth,
        target:
          target === 'existing'
            ? { kind: 'existing', clientId }
            : {
                kind: 'new',
                account: {
                  name: name.trim(),
                  legalName: legalName.trim(),
                  billingEmail: billingEmail.trim(),
                  billingAddress: addressFilled
                    ? {
                        street: address.street.trim(),
                        zipCode: address.zipCode.trim(),
                        city: address.city.trim(),
                        countryCode: address.countryCode.trim().toUpperCase(),
                      }
                    : null,
                  safetyCap: parseEuroInput(safetyCap) ?? '0',
                  clientCap: parseEuroInput(clientCap) ?? null,
                  defaultUserCap: parseEuroInput(userCap) ?? null,
                },
              },
        issueMandateLink: mandate,
      };
      return crmSend<ConvertDealResult>('POST', `/deals/${deal.id}/convert`, body);
    },
    onSuccess: (result) => {
      refresh({ clients: true });
      onDone(result);
    },
  });

  return (
    <>
      <form
        id="convert-form"
        className="contents"
        onSubmit={(event) => {
          event.preventDefault();
          setSubmitted(true);
          if (valid) {
            convert.mutate();
          }
        }}
      >
        <DialogBody>
          <Fieldset title="Affaire gagnée">
            <dl className="m-0 grid grid-cols-3 gap-3">
              <div className="grid gap-0.5">
                <dt className="text-label text-muted-foreground">Mise en place HT</dt>
                <dd className="m-0 font-mono text-sm font-medium tabular-nums">
                  {eurOrDash(deal.setup)}
                </dd>
              </div>
              <div className="grid gap-0.5">
                <dt className="text-label text-muted-foreground">Abonnement HT</dt>
                <dd className="m-0 font-mono text-sm font-medium tabular-nums">
                  {deal.monthly === null ? '—' : `${eurOrDash(deal.monthly)} / mois`}
                </dd>
              </div>
              <div className="grid gap-0.5">
                <dt className="text-label text-muted-foreground">Modèle</dt>
                <dd className="m-0 text-sm">{MODEL_LABEL[deal.model]}</dd>
              </div>
            </dl>
            <Fields2>
              <Field label="Date de signature" required error={shown(dateErrors.signedOn)}>
                <DateInput
                  value={signedOn}
                  onChange={(value) => {
                    setSignedOn(value);
                  }}
                />
              </Field>
              <Field
                label="Début de l’abonnement"
                required
                error={shown(dateErrors.startMonth)}
                hint="Premier mois facturé de l’abonnement."
              >
                <MonthInput
                  value={startMonth}
                  onChange={(value) => {
                    setStartMonth(value);
                  }}
                />
              </Field>
            </Fields2>
          </Fieldset>

          {canUseExisting ? (
            <div className="grid gap-1.5">
              <Label id="convert-target-label">Compte facturé</Label>
              <Choices<Target>
                labelledBy="convert-target-label"
                value={target}
                onChange={setTarget}
                items={[
                  {
                    value: 'existing',
                    label: 'Compte existant',
                    hint: 'L’abonnement s’ajoute à un compte du client.',
                  },
                  {
                    value: 'new',
                    label: 'Nouveau compte',
                    hint: 'Un compte facturé de plus est créé.',
                  },
                ]}
              />
            </div>
          ) : null}

          {target === 'existing' ? (
            <Fieldset title="Compte facturé">
              <Field label="Compte" required error={fieldError('clientId')}>
                <NativeSelect
                  value={clientId}
                  onChange={(event) => {
                    setClientId(event.target.value);
                  }}
                >
                  {company.accounts.map((account) => (
                    <option key={account.id} value={account.id}>
                      {account.name}
                    </option>
                  ))}
                </NativeSelect>
              </Field>
            </Fieldset>
          ) : (
            <Fieldset title="Compte facturé">
              <Fields2>
                <Field label="Nom" required error={fieldError('name')}>
                  <Input
                    value={name}
                    maxLength={200}
                    onChange={(event) => {
                      setName(event.target.value);
                    }}
                  />
                </Field>
                <Field label="Raison sociale" required error={fieldError('legalName')}>
                  <Input
                    value={legalName}
                    maxLength={200}
                    onChange={(event) => {
                      setLegalName(event.target.value);
                    }}
                  />
                </Field>
              </Fields2>
              <Field
                label="Email de facturation"
                required
                error={fieldError('billingEmail')}
                hint={
                  recipient !== null
                    ? `Repris du contact ${recipient.firstName} ${recipient.lastName}${recipient.roles.includes('billing') ? ', rôle Facturation' : ', contact principal'}.`
                    : undefined
                }
              >
                <Input
                  type="email"
                  value={billingEmail}
                  onChange={(event) => {
                    setBillingEmail(event.target.value);
                  }}
                />
              </Field>
              <Field
                label="Plafond de sécurité"
                required
                error={fieldError('safetyCap')}
                hint="Une alerte part à 80 %. Au-delà du plafond, les requêtes sont rejetées."
              >
                <InputGroup suffix="€" className="max-w-60">
                  <Input
                    numeric
                    inputMode="decimal"
                    value={safetyCap}
                    onChange={(event) => {
                      setSafetyCap(event.target.value);
                    }}
                  />
                </InputGroup>
              </Field>
              <Fields2>
                <Field label="Plafond client" error={fieldError('clientCap')}>
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      inputMode="decimal"
                      placeholder="Optionnel"
                      value={clientCap}
                      onChange={(event) => {
                        setClientCap(event.target.value);
                      }}
                    />
                  </InputGroup>
                </Field>
                <Field label="Plafond utilisateur par défaut" error={fieldError('userCap')}>
                  <InputGroup suffix="€">
                    <Input
                      numeric
                      inputMode="decimal"
                      placeholder="Optionnel"
                      value={userCap}
                      onChange={(event) => {
                        setUserCap(event.target.value);
                      }}
                    />
                  </InputGroup>
                </Field>
              </Fields2>
            </Fieldset>
          )}

          {target === 'new' ? (
            <Fieldset
              title="Adresse de facturation"
              description={
                found !== null
                  ? 'Reprise de l’annuaire des entreprises. Utilisée seulement si Qonto ne connaît pas encore ce SIREN.'
                  : 'Utilisée seulement si Qonto ne connaît pas encore ce SIREN.'
              }
            >
              <Field label="Rue" error={fieldError('address')}>
                <Input
                  value={address.street}
                  maxLength={250}
                  onChange={(event) => {
                    setAddress({ ...address, street: event.target.value });
                  }}
                />
              </Field>
              <Fields2>
                <Field label="Code postal">
                  <Input
                    value={address.zipCode}
                    maxLength={20}
                    onChange={(event) => {
                      setAddress({ ...address, zipCode: event.target.value });
                    }}
                  />
                </Field>
                <Field label="Ville">
                  <Input
                    value={address.city}
                    maxLength={50}
                    onChange={(event) => {
                      setAddress({ ...address, city: event.target.value });
                    }}
                  />
                </Field>
              </Fields2>
            </Fieldset>
          ) : null}

          <Fieldset title="Encaissement">
            <CheckRow
              label={
                recipient !== null
                  ? `Émettre le lien de mandat SEPA à envoyer à ${recipient.firstName} ${recipient.lastName}`
                  : 'Émettre le lien de mandat SEPA'
              }
              checked={mandate}
              onChange={(event) => {
                setMandate(event.target.checked);
              }}
            />
            <FieldHint>
              Le premier prélèvement part après signature du mandat. La mise en place est facturée
              séparément.
            </FieldHint>
          </Fieldset>

          <Callout variant="info">
            L’entreprise passe en statut Client. Son historique commercial reste attaché à la fiche,
            et le compte facturé apparaît dans Clients.
          </Callout>
          <MutationError
            message={
              convert.isError ? errorMessage(convert.error, 'Échec du passage en client.') : null
            }
          />
        </DialogBody>
      </form>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Annuler
        </Button>
        <Button type="submit" form="convert-form" size="lg" disabled={convert.isPending}>
          Passer en client
        </Button>
      </DialogFooter>
    </>
  );
}

function ConvertResult({
  result,
  onClose,
}: {
  result: ConvertDealResult;
  onClose: () => void;
}): JSX.Element {
  const navigate = useNavigate();
  const first = result.accounts[0];
  const recipient = result.mandateRecipient;
  return (
    <DialogContent aria-describedby={undefined}>
      <DialogHead title="Affaire gagnée" description="Le client est créé dans la passerelle." />
      <DialogBody>
        <Fieldset title={result.accounts.length > 1 ? 'Comptes facturés' : 'Compte facturé'}>
          <ul className="m-0 grid list-none gap-1 p-0 text-[13px]">
            {result.accounts.map((account) => (
              <li key={account.id}>
                <Link
                  to="/clients/$clientId"
                  params={{ clientId: account.id }}
                  className="font-medium underline underline-offset-[3px]"
                >
                  {account.name}
                </Link>
              </li>
            ))}
          </ul>
        </Fieldset>
        {result.mandateUrl !== null ? (
          <Fieldset
            title="Lien de mandat SEPA"
            description={
              recipient !== null
                ? `À envoyer à ${recipient.name} (${recipient.email}).`
                : 'À envoyer au contact de facturation.'
            }
          >
            <div className="flex items-center gap-2">
              <Input readOnly value={result.mandateUrl} className="font-mono text-xs" />
              <CopyButton text={result.mandateUrl} label="Copier le lien de mandat" />
            </div>
            {recipient !== null ? (
              <Button asChild variant="outline" size="sm" className="justify-self-start">
                <a
                  href={`mailto:${recipient.email}?subject=${encodeURIComponent('Mandat de prélèvement SEPA')}&body=${encodeURIComponent(`Bonjour ${recipient.name},\n\nVoici le lien pour signer le mandat de prélèvement SEPA :\n${result.mandateUrl}\n`)}`}
                >
                  <Mail aria-hidden="true" />
                  Écrire à {recipient.name}
                </a>
              </Button>
            ) : null}
          </Fieldset>
        ) : null}
      </DialogBody>
      <DialogFooter>
        <Button type="button" variant="outline" onClick={onClose}>
          Fermer
        </Button>
        {first !== undefined ? (
          <Button
            type="button"
            size="lg"
            onClick={() => {
              onClose();
              void navigate({ to: '/clients/$clientId', params: { clientId: first.id } });
            }}
          >
            Voir le client
          </Button>
        ) : null}
      </DialogFooter>
    </DialogContent>
  );
}
